/**
 * FoundersMessagePage — long-form per-service founder narratives.
 *
 * Routes:
 *   - `/:lang/founders-message`               → hub (list of available)
 *   - `/:lang/founders-message/:service`      → specific service message
 *
 * Reached from the footer, NOT the top nav (owner directive).
 *
 * Content comes from `/data/foundersMessages.js`. Visuals borrow the
 * japancardetc.com / japantollreceipts.jp founder page aesthetic:
 *   - Dark navy hero panel with portrait left + name/title right
 *   - Eyebrow → big condensed headline → lead paragraph → pull quote
 *   - Numbered section list with eyebrow / title / body
 *   - Sign-off and CTA back to /services or /testimonials
 */
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, Quote, ShieldCheck } from "lucide-react";

import SEO, { PAGE_SEO } from "@/components/SEO";
import JsonLd, { breadcrumbSchema, personSchema } from "@/components/JsonLd";
import PageSideAura from "@/components/motion/PageSideAura";
import Reveal from "@/components/motion/Reveal";
import PeopleStoryTabs from "@/components/PeopleStoryTabs";
import SafeImage from "@/components/SafeImage";
import {
  FOUNDER,
  FOUNDERS_MESSAGES,
} from "@/data/foundersMessages";

function pickL(en, ja, lang) {
  return lang === "ja" ? ja : en;
}

export default function FoundersMessagePage() {
  const { lang = "en", service } = useParams();
  const isJa = lang === "ja";

  // Hub removed per owner directive — redirect to the default JapanETCcard story.
  if (!service) {
    return <Navigate to={`/${lang}/founders-message/japanetccard`} replace />;
  }

  const data = FOUNDERS_MESSAGES[service];

  if (!data) {
    return <FoundersMissing lang={lang} isJa={isJa} service={service} />;
  }

  return <FoundersStory lang={lang} isJa={isJa} service={service} data={data} />;
}

/* ============================================================
   STORY — full founder's message for a given service
============================================================ */


/* ============================================================
   STORY — full founder's message for a given service
============================================================ */
function FoundersStory({ lang, isJa, service, data }) {
  const accent = data.accent;
  const softBg = data.accentSoft;

  return (
    <article className="bg-white" data-testid={`founders-message-${service}`}>
      <SEO
        {...(service === "japantollreceipts"
          ? PAGE_SEO.foundersMessageJTR[lang]
          : PAGE_SEO.foundersMessageJEC[lang])}
        path={`/${lang}/founders-message/${service}`}
        lang={lang}
      />
      <JsonLd
        data={[
          breadcrumbSchema([
            { name: isJa ? "ホーム" : "Home", url: `/${lang}` },
            { name: isJa ? "代表メッセージ" : "Founder's Message", url: `/${lang}/founders-message/japanetccard` },
            {
              name: service === "japantollreceipts" ? "JapanTollReceipts" : "JapanETCcard",
              url: `/${lang}/founders-message/${service}`,
            },
          ]),
          personSchema({
            name: "Adam Jones",
            jobTitle: isJa ? "創業者・代表取締役" : "Founder & Representative Director",
          }),
        ]}
      />

      {/* Preload the service wordmark so the founder-page brand header
            never shows a "load big → shrink down" flicker on first paint.
            Combined with intrinsic width/height on the <img loading="lazy" decoding="async">, this gives
            the browser everything it needs to layout + paint instantly. */}
      {data.wordmark && (
        <link
          rel="preload"
          as="image"
          href={data.wordmark}
          imageSrcSet={data.wordmark}
          fetchPriority="high"
        />
      )}

      <PeopleStoryTabs lang={lang} />

      {/* SERVICE BRAND HEADER — wordmark + graphic strip so the
          founder's-message page is immediately recognizable as
          belonging to JapanETCcard vs JapanTollReceipts, etc. */}
      <section
        className="border-b border-[#E2E8F0]"
        style={{ backgroundColor: softBg }}
        data-testid={`founders-message-${service}-brand-header`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 md:py-8">
          <div className="flex items-center justify-between gap-6 flex-wrap">
            <div className="flex items-center gap-4 md:gap-5">
              {data.wordmark && (
                <img
                  src={data.wordmark}
                  alt={`${data.serviceLabel} logo`}
                  className={`${data.wordmarkClassName || "h-9 md:h-11"} w-auto`}
                  // Intrinsic dimensions reserve the correct slot on
                  // first paint so the wordmark never "loads big →
                  // shrinks down" (which was causing a visible flicker
                  // on the founders' message header). Width/height
                  // values match the actual file dimensions of the
                  // optimized PNGs in /public/brand/.
                  width={data.wordmarkWidth || 698}
                  height={data.wordmarkHeight || 88}
                  loading="eager"
                  decoding="sync"
                  fetchPriority="high"
                  draggable={false}
                />
              )}
              <div className="hidden sm:block h-8 w-px" style={{ backgroundColor: accent, opacity: 0.25 }} />
              {/* Eyebrow + service label is hidden on mobile — the
                  wordmark above already shows the service brand, and
                  this block was the culprit behind a 50px horizontal
                  overflow on /founders-message/japantollreceipts at
                  390px viewport (wide letter-spacing on the eyebrow
                  pushed the row past the viewport). */}
              <div className="hidden sm:block">
                <div
                  className="text-[10px] tracking-[0.28em] font-mono-display uppercase font-bold"
                  style={{ color: accent }}
                >
                  {isJa ? "代表メッセージ" : "Founder's Message"}
                </div>
                <div className="text-[14.5px] font-bold tracking-tight text-[#0F1D2D] mt-0.5">
                  {data.serviceLabel}
                </div>
              </div>
            </div>
            {data.heroGraphic && (
              <img
                src={data.heroGraphic}
                alt={pickL(
                  data.heroGraphicAltEn || "Service graphic",
                  data.heroGraphicAltJa || "サービスのビジュアル",
                  lang
                )}
                className="h-12 md:h-14 w-auto opacity-90"
                loading="eager"
                draggable={false}
              />
            )}
          </div>
        </div>
      </section>

      {/* HERO */}
      <section className="bg-[#5B2E83] text-white relative overflow-hidden">
        <span
          aria-hidden
          className="absolute -right-32 -top-32 w-[460px] h-[460px] rounded-full opacity-25 blur-3xl"
          style={{ backgroundColor: accent }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* Portrait */}
            <div className="lg:col-span-4 order-1">
              <Reveal variant="up">
                <div
                  className="relative w-full max-w-[360px] aspect-[3/4] rounded-2xl overflow-hidden ring-2"
                  style={{ borderColor: accent, boxShadow: `0 24px 60px -30px ${accent}` }}
                  data-testid={`founders-message-${service}-portrait`}
                >
                  <SafeImage
                    src={FOUNDER.portrait}
                    alt={`${FOUNDER.name} — ${FOUNDER.title}`}
                    imgClassName="block w-full h-full object-cover"
                    fallbackClassName="w-full h-full flex items-center justify-center bg-[#0F1D2D]/15"
                    loading="eager"
                  />
                  <span
                    aria-hidden
                    className="absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(180deg, transparent 60%, rgba(15,29,45,0.7) 100%)",
                    }}
                  />
                  <span
                    className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] tracking-[0.22em] font-mono-display uppercase font-bold"
                    style={{ backgroundColor: accent, color: "#fff" }}
                  >
                    {isJa ? "創業者 ・ CEO" : "Founder & CEO"}
                  </span>
                </div>
              </Reveal>
            </div>

            {/* Title + lead */}
            <div className="lg:col-span-8 order-2">
              <Reveal variant="up">
                <div
                  data-testid={`founders-message-${service}-eyebrow`}
                  className="text-[10px] tracking-[0.28em] font-mono-display uppercase font-bold mb-3 text-white/85"
                >
                  <span
                    className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full ring-1"
                    style={{ backgroundColor: `${accent}22`, borderColor: `${accent}66` }}
                  >
                    <span
                      aria-hidden
                      className="inline-block h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: accent }}
                    />
                    <span className="text-white">{pickL(data.eyebrowEn, data.eyebrowJa, lang)}</span>
                    <span className="text-white/55">·</span>
                    <span className="text-white">{data.serviceLabel}</span>
                  </span>
                </div>
                <h1
                  data-testid={`founders-message-${service}-h1`}
                  className={[
                    isJa ? "font-ja" : "font-display",
                    "text-white text-3xl md:text-5xl lg:text-[56px] font-bold tracking-tighter leading-[1.05]",
                  ].join(" ")}
                >
                  {pickL(data.titleEn, data.titleJa, lang)}
                </h1>
                <p className="mt-6 text-[16px] md:text-[17px] leading-[1.85] text-white/80 max-w-3xl">
                  {pickL(data.leadEn, data.leadJa, lang)}
                </p>
                <div className="mt-7 inline-flex items-center gap-3 flex-wrap">
                  <span className="font-display text-[18px] font-bold text-white">
                    {FOUNDER.name}
                  </span>
                  <span className="text-white/35">·</span>
                  <span className="text-[12px] tracking-[0.18em] font-mono-display uppercase font-semibold text-white/90">
                    {FOUNDER.title}
                  </span>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* PULL QUOTE */}
      {data.pullQuoteEn && (
        <section className="relative isolate bg-white border-b border-[#E2E8F0]">
          <PageSideAura testId="founders-message-pullquote-aura" />
          <div className="max-w-5xl mx-auto px-6 md:px-10 py-10 md:py-14">
            <Reveal variant="up">
              <div
                className="relative rounded-3xl overflow-hidden"
                style={{ backgroundColor: softBg }}
              >
                <Quote
                  aria-hidden
                  className="absolute -top-3 -left-2 h-24 w-24 opacity-[0.12]"
                  style={{ color: accent }}
                  strokeWidth={1}
                />
                <div className="relative px-6 md:px-12 py-10 md:py-14">
                  <span
                    aria-hidden
                    className="absolute left-0 top-6 bottom-6 w-1 rounded-full"
                    style={{ backgroundColor: accent }}
                  />
                  <blockquote
                    className={[
                      isJa ? "font-ja" : "font-display",
                      "text-[#0F1D2D] text-[22px] md:text-[30px] lg:text-[34px] font-medium leading-[1.35] tracking-tight italic",
                    ].join(" ")}
                    data-testid={`founders-message-${service}-pullquote`}
                  >
                    “{pickL(data.pullQuoteEn, data.pullQuoteJa, lang)}”
                  </blockquote>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* BODY SECTIONS */}
      <section className="relative isolate bg-white">
        <PageSideAura testId="founders-message-body-aura" />
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <ol
            className="space-y-12 md:space-y-16"
            data-testid={`founders-message-${service}-sections`}
          >
            {data.sections.map((sec, i) => (
              <Reveal as="li" variant="up" delay={i * 60} key={i}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10">
                  <div className="md:col-span-3">
                    <div
                      className="text-[10px] tracking-[0.28em] font-mono-display uppercase font-bold"
                      style={{ color: accent }}
                    >
                      {pickL(sec.eyebrowEn, sec.eyebrowJa, lang)}
                    </div>
                  </div>
                  <div className="md:col-span-9">
                    <h2
                      className={[
                        isJa ? "font-ja" : "font-display",
                        "text-[#0F1D2D] text-2xl md:text-[30px] lg:text-[34px] font-bold tracking-tight leading-[1.2]",
                      ].join(" ")}
                    >
                      {pickL(sec.titleEn, sec.titleJa, lang)}
                    </h2>
                    {/* Body — accepts either a single string (one paragraph)
                        or an array of strings (renders one <p> per item with
                        consistent rhythm). Array form is preferred for long
                        narrative sections so the reader has breathing room. */}
                    {(() => {
                      const body = pickL(sec.bodyEn, sec.bodyJa, lang);
                      const paragraphs = Array.isArray(body) ? body : [body];
                      return paragraphs.map((para, pi) => (
                        <p
                          key={pi}
                          className={[
                            pi === 0 ? "mt-5" : "mt-4",
                            "text-[15.5px] md:text-[16px] leading-[1.9] text-[#334155]",
                          ].join(" ")}
                        >
                          {para}
                        </p>
                      ));
                    })()}

                    {/* Optional in-section CTA — used to point readers
                        from a story moment to a relevant destination on
                        the site (e.g. "View the Okinawa signboards"). */}
                    {(sec.ctaEn || sec.ctaJa) && (() => {
                      const cta = (lang === "ja" ? sec.ctaJa : sec.ctaEn) || sec.ctaEn || sec.ctaJa;
                      if (!cta?.href || !cta?.label) return null;
                      const external = /^https?:\/\//.test(cta.href);
                      const className =
                        "mt-6 inline-flex items-center gap-2 text-[14px] md:text-[14.5px] font-semibold text-[#0F1D2D] hover:text-[#5B2E83] transition-colors border-b-2 border-[#5B2E83] pb-1";
                      return external ? (
                        <a
                          href={cta.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={className}
                          data-testid={`founders-message-${service}-section-${i}-cta`}
                        >
                          {cta.label}
                        </a>
                      ) : (
                        <Link
                          to={`/${lang}${cta.href.startsWith("/") ? cta.href : "/" + cta.href}`}
                          className={className}
                          data-testid={`founders-message-${service}-section-${i}-cta`}
                        >
                          {cta.label}
                        </Link>
                      );
                    })()}

                    {/* Single inline image — for sections that have one
                        archival photo with its own caption. */}
                    {sec.image?.src && (
                      <figure
                        className="mt-8 md:mt-10"
                        data-testid={`founders-message-${service}-section-${i}-image`}
                      >
                        <div className="overflow-hidden rounded-2xl ring-1 ring-[#E2E8F0] bg-[#F8FAFC] shadow-[0_18px_40px_-22px_rgba(15,29,45,0.28)]">
                          <SafeImage
                            src={sec.image.src}
                            alt={pickL(sec.image.altEn, sec.image.altJa, lang)}
                            imgClassName="block w-full h-auto"
                            fallbackClassName="w-full aspect-[4/3] flex items-center justify-center bg-[#F8FAFC]"
                            loading="lazy"
                          />
                        </div>
                        {(sec.image.captionEn || sec.image.captionJa) && (
                          <figcaption className="mt-3 text-[12.5px] md:text-[13px] leading-[1.7] text-[#64748B] italic max-w-2xl">
                            {pickL(sec.image.captionEn, sec.image.captionJa, lang)}
                          </figcaption>
                        )}
                      </figure>
                    )}

                    {/* Gallery — supports two layouts per item:
                          - Single image: { src, alt*, caption* } → renders
                            one full-width photo with its caption.
                          - Pair: { pair: [imgA, imgB], caption* } → renders
                            both photos side-by-side (50/50 on tablet+, stacked
                            on mobile) with one combined caption below. Useful
                            for portrait shots that read better as a diptych
                            than as two separate full-width tiles. */}
                    {sec.gallery?.length > 0 && (
                      <div
                        className="mt-8 md:mt-10 space-y-8 md:space-y-10"
                        data-testid={`founders-message-${service}-section-${i}-gallery`}
                      >
                        {sec.gallery.map((item, gi) => {
                          const isPair = Array.isArray(item.pair) && item.pair.length === 2;
                          return (
                            <figure key={gi}>
                              {isPair ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                                  {item.pair.map((img, pi) => (
                                    <div
                                      key={pi}
                                      className="overflow-hidden rounded-2xl ring-1 ring-[#E2E8F0] bg-[#F8FAFC] shadow-[0_18px_40px_-22px_rgba(15,29,45,0.28)] flex items-center justify-center"
                                    >
                                      <SafeImage
                                        src={img.src}
                                        alt={pickL(img.altEn, img.altJa, lang)}
                                        imgClassName="block max-h-[560px] w-auto h-auto max-w-full object-contain"
                                        fallbackClassName="w-full h-[260px] flex items-center justify-center bg-[#F8FAFC]"
                                        loading="lazy"
                                        decoding="async"
                                      />
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="overflow-hidden rounded-2xl ring-1 ring-[#E2E8F0] bg-[#F8FAFC] shadow-[0_18px_40px_-22px_rgba(15,29,45,0.28)] flex items-center justify-center">
                                  <SafeImage
                                    src={item.src}
                                    alt={pickL(item.altEn, item.altJa, lang)}
                                    imgClassName="block max-h-[560px] w-auto h-auto max-w-full object-contain"
                                    fallbackClassName="w-full h-[280px] flex items-center justify-center bg-[#F8FAFC]"
                                    loading="lazy"
                                    decoding="async"
                                  />
                                </div>
                              )}
                              {(item.captionEn || item.captionJa) && (
                                <figcaption className="mt-3 text-[13px] md:text-[14px] leading-[1.75] text-[#475569] max-w-2xl">
                                  {pickL(item.captionEn, item.captionJa, lang)}
                                </figcaption>
                              )}
                            </figure>
                          );
                        })}
                      </div>
                    )}

                    {/* Social-post quote — preserves an authentic, dated
                        social-media post in its original voice. Optional
                        `image` shows the original photo from the post,
                        side-by-side with the quote (the two go hand in
                        hand). Rendered as a single highlighted callout. */}
                    {sec.socialQuote && (
                      <figure
                        className="mt-8 md:mt-10 relative rounded-2xl ring-1 ring-[#E2E8F0] bg-gradient-to-br from-[#FAFBFD] to-[#F4F0FA] overflow-hidden shadow-[0_18px_44px_-24px_rgba(91,46,131,0.25)]"
                        style={{ borderLeft: `4px solid ${accent}` }}
                        data-testid={`founders-message-${service}-section-${i}-social-quote`}
                      >
                        <div
                          className={[
                            "grid items-stretch",
                            sec.socialQuote.image
                              ? "grid-cols-1 sm:grid-cols-[180px_1fr] md:grid-cols-[220px_1fr]"
                              : "grid-cols-1",
                          ].join(" ")}
                        >
                          {sec.socialQuote.image && (
                            <div className="relative bg-[#0F1D2D]/4 sm:border-r sm:border-[#E8ECF2]">
                              <SafeImage
                                src={sec.socialQuote.image.src}
                                alt={pickL(sec.socialQuote.image.altEn, sec.socialQuote.image.altJa, lang)}
                                imgClassName="block w-full h-44 sm:h-full object-cover"
                                fallbackClassName="w-full h-44 sm:h-full flex items-center justify-center bg-[#0F1D2D]/8"
                                loading="lazy"
                                decoding="async"
                              />
                            </div>
                          )}
                          <blockquote className="px-5 md:px-7 py-5 md:py-6">
                            <div className="flex flex-wrap items-center gap-2 md:gap-2.5 mb-3">
                              <span
                                className="text-[10px] tracking-[0.22em] uppercase font-mono-display font-bold"
                                style={{ color: accent }}
                              >
                                {isJa ? "本人投稿（当時のSNS）" : "Posted on social media"}
                              </span>
                              <span className="text-[#CBD5E1]">·</span>
                              <span className="text-[12px] text-[#64748B] font-medium">
                                {sec.socialQuote.dateLabel}
                              </span>
                            </div>
                            <p
                              className={[
                                isJa ? "font-ja" : "font-display",
                                "text-[#0F1D2D] text-[15px] md:text-[16.5px] leading-[1.7] italic",
                              ].join(" ")}
                            >
                              “{pickL(sec.socialQuote.textEn, sec.socialQuote.textJa, lang)}”
                            </p>
                            <footer className="mt-3 text-[12px] text-[#64748B] font-medium">
                              — {FOUNDER.name}, {sec.socialQuote.dateLabel}
                            </footer>
                          </blockquote>
                        </div>
                      </figure>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* SIGN-OFF */}
      <section style={{ backgroundColor: softBg }}>
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <Reveal variant="up">
            <p
              className={[
                isJa ? "font-ja" : "font-display",
                "text-[#0F1D2D] text-[18px] md:text-[22px] leading-[1.65] tracking-tight",
              ].join(" ")}
            >
              {pickL(data.signOffEn, data.signOffJa, lang)}
            </p>
            <div className="mt-8 flex items-center gap-4">
              <SafeImage
                src={FOUNDER.portrait}
                alt={`${FOUNDER.name} — ${isJa ? "創業者・代表取締役" : "Founder & Representative Director"}, NoJo Enterprise`}
                imgClassName="h-14 w-14 rounded-full object-cover ring-2"
                fallbackClassName="h-14 w-14 rounded-full flex items-center justify-center bg-[#0F1D2D]/15"
                fallbackIconClassName="h-5 w-5"
                loading="lazy"
                decoding="async"
                style={{ borderColor: accent }}
              />
              <div>
                <div className="font-display text-[18px] font-bold text-[#0F1D2D]">
                  {FOUNDER.name}
                </div>
                <div
                  className="text-[11.5px] tracking-[0.18em] font-mono-display uppercase font-semibold"
                  style={{ color: accent }}
                >
                  {FOUNDER.title}
                </div>
                <div className="mt-1 text-[12px] text-[#64748B]">
                  {FOUNDER.affiliations}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA + DISCLAIMER */}
      <section className="bg-[#5B2E83] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-14 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
          <div className="lg:col-span-1 flex lg:justify-center">
            <ShieldCheck className="h-10 w-10 text-[#7AB8E8]" strokeWidth={1.5} />
          </div>
          <div className="lg:col-span-7">
            <div className="text-[10px] tracking-[0.28em] font-mono-display text-[#94A3B8] uppercase font-bold mb-2">
              {isJa ? "独立したサービスです" : "Independent service"}
            </div>
            <p className="text-[14px] leading-[1.75] text-[#CBD5E1]">
              {isJa
                ? "本サービスはNoJo Enterpriseが独立して運営しています。NEXCO、ETC利用照会サービス、各高速道路会社、銀行、行政機関の公式サービスではありません。"
                : "Operated independently by NoJo Enterprise. Not an official service of NEXCO, ETC Usage Inquiry Service, any toll-road operator, bank, or government agency."}
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end flex-wrap gap-3">
            <Link
              to={`/${lang}/services/${service}`}
              data-testid={`founders-message-${service}-cta-service`}
              className="inline-flex items-center gap-2 bg-white text-[#0F1D2D] px-5 py-3 rounded-full text-[13px] font-semibold hover:bg-[#F1F5F9] transition-colors"
            >
              {isJa ? "サービス詳細を見る" : "View service detail"}
              <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
            </Link>
            <Link
              to={`/${lang}/founders-message/${service === "japantollreceipts" ? "japanetccard" : "japantollreceipts"}`}
              data-testid={`founders-message-${service}-cta-back`}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-[13px] font-semibold ring-1 ring-white/30 text-white hover:bg-white/10 transition-colors"
            >
              {isJa
                ? service === "japantollreceipts"
                  ? "JapanETCcard の代表メッセージへ"
                  : "JapanTollReceipts の代表メッセージへ"
                : service === "japantollreceipts"
                  ? "Read the JapanETCcard message"
                  : "Read the JapanTollReceipts message"}
              <ArrowRight className="h-4 w-4" strokeWidth={2.25} />
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
}

function FoundersMissing({ lang, isJa, service }) {
  return (
    <article className="bg-white min-h-[60vh] flex items-center" data-testid="founders-message-missing">
      <div className="max-w-3xl mx-auto px-6 py-12 text-center">
        <div className="text-[10px] tracking-[0.28em] font-mono-display text-[#94A3B8] uppercase font-bold mb-3">
          {isJa ? "準備中" : "In preparation"}
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0F1D2D]">
          {isJa
            ? `${service} の代表メッセージは現在準備中です。`
            : `The founder's message for ${service} is being prepared.`}
        </h1>
        <Link
          to={`/${lang}/founders-message`}
          className="mt-8 inline-flex items-center gap-2 text-[13px] font-semibold text-[#0E5FB0]"
        >
          {isJa ? "他の代表メッセージを見る" : "See other founder's messages"}
          <ArrowRight className="h-4 w-4" strokeWidth={2} />
        </Link>
      </div>
    </article>
  );
}
